import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';

export const TENANT_COLOR_PRESETS = [
  '#1976d2',
  '#1565c0',
  '#0d47a1',
  '#0288d1',
  '#0097a7',
  '#00838f',
  '#2e7d32',
  '#388e3c',
  '#558b2f',
  '#f9a825',
  '#f57c00',
  '#ef6c00',
  '#e65100',
  '#d32f2f',
  '#c62828',
  '#7b1fa2',
  '#6a1b9a',
  '#2a2d64',
  '#1a237e',
  '#37474f',
  '#455a64',
  '#000000',
  '#546e7a',
  '#eceff1',
  '#ffffff',
];

/** Normalize hex for API/DB; empty string means “no color”. */
export function normalizeBrandingColorForApi(v) {
  const t = v == null ? '' : String(v).trim();
  if (!t) return '';
  if (/^#[0-9A-Fa-f]{6}$/i.test(t)) return t.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/i.test(t)) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return t;
}

function toColorInputValue(hex) {
  if (!hex || typeof hex !== 'string') return '#000000';
  const s = hex.trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/i.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return (`#${r}${r}${g}${g}${b}${b}`).toLowerCase();
  }
  return '#000000';
}

function swatchBackground(v) {
  const t = (v || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(t)) return t;
  if (/^#[0-9A-Fa-f]{3}$/i.test(t)) return toColorInputValue(t);
  if (/^[0-9A-Fa-f]{6}$/i.test(t)) return `#${t}`;
  return '#e0e0e0';
}

function isLightHex(hex) {
  const v = toColorInputValue(hex);
  const n = parseInt(v.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.85;
}

/**
 * Text field + swatch; hover (or click swatch) opens preset grid + native color picker.
 * Integrates with Formik via setFieldValue(name, hex).
 */
const TenantBrandingColorField = ({
  name,
  label,
  value,
  setFieldValue,
  onBlur,
  error,
  helperText,
  placeholder = '#1976d2',
}) => {
  const wrapRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const [open, setOpen] = useState(false);

  const cancelTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };

  useEffect(() => () => cancelTimers(), []);

  const scheduleOpen = () => {
    cancelTimers();
    openTimer.current = setTimeout(() => setOpen(true), 160);
  };

  const scheduleClose = () => {
    cancelTimers();
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  };

  const displaySwatch = swatchBackground(value);
  const swatchBorder = isLightHex(displaySwatch)
    ? '1px solid rgba(0,0,0,0.25)'
    : '1px solid rgba(255,255,255,0.2)';

  return (
    <Box
      ref={wrapRef}
      sx={{ position: 'relative', width: '100%' }}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
    >
      <TextField
        fullWidth
        margin="dense"
        name={name}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setFieldValue(name, e.target.value)}
        onBlur={onBlur}
        error={Boolean(error)}
        helperText={helperText}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box
                component="button"
                type="button"
                tabIndex={-1}
                aria-label={`Open color palette for ${label}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  cancelTimers();
                  setOpen((o) => !o);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: displaySwatch,
                  border: swatchBorder,
                  cursor: 'pointer',
                  p: 0,
                  flexShrink: 0,
                }}
              />
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={open && Boolean(wrapRef.current)}
        anchorEl={wrapRef.current}
        placement="bottom-start"
        disablePortal
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <Paper
          elevation={8}
          sx={{ p: 1.5, mt: 0.5, maxWidth: 260 }}
          onMouseEnter={cancelTimers}
          onMouseLeave={scheduleClose}
        >
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Presets — click to apply
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {TENANT_COLOR_PRESETS.map((c) => (
              <Box
                key={c}
                component="button"
                type="button"
                title={c}
                aria-label={c}
                onClick={() => {
                  setFieldValue(name, c);
                  setOpen(false);
                }}
                sx={{
                  width: 26,
                  height: 26,
                  bgcolor: c,
                  border: '1px solid',
                  borderColor: isLightHex(c) ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.15)',
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  p: 0,
                  minWidth: 0,
                  '&:hover': { transform: 'scale(1.08)' },
                }}
              />
            ))}
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Custom
            </Typography>
            <input
              type="color"
              value={toColorInputValue(value)}
              onChange={(e) => setFieldValue(name, e.target.value)}
              style={{
                width: 36,
                height: 28,
                padding: 0,
                border: 'none',
                cursor: 'pointer',
                background: 'transparent',
              }}
            />
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};

export default TenantBrandingColorField;
